import { memo, useState } from "react";
import { Link } from "wouter";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown } from "lucide-react";
import {
  PALLET_GRADE_LABEL,
  FACTORY_REFURBISHED_GRADING_ANCHOR,
} from "@shared/palletDisplay";

const DEFAULT_BRANDS_VISIBLE = 5;

export interface SubcategoryFilterBlockProps {
  catId: string;
  categoryName: string;
  brands: string[];
  grades: string[];
  selectedBrands: string[];
  selectedGrades: string[];
  brandCounts: Record<string, number>;
  gradeCounts: Record<string, number>;
  totalCount: number;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onToggleBrand: (brand: string) => void;
  onToggleGrade: (grade: string) => void;
  onSelectAllBrands: () => void;
  onClearBrands: () => void;
  onSelectAllGrades: () => void;
  onClearGrades: () => void;
  CategoryIcon: React.ComponentType<{ className?: string }>;
  aiIconUrl?: string | null;
}

function SubcategoryFilterBlockInner({
  catId,
  categoryName,
  brands,
  grades,
  selectedBrands,
  selectedGrades,
  brandCounts,
  gradeCounts,
  totalCount,
  isExpanded,
  onToggleExpanded,
  onToggleBrand,
  onToggleGrade,
  onSelectAllBrands,
  onClearBrands,
  onSelectAllGrades,
  onClearGrades,
  CategoryIcon,
  aiIconUrl,
}: SubcategoryFilterBlockProps) {
  const [showAllBrands, setShowAllBrands] = useState(false);

  const allBrandsSelected =
    brands.length > 0 && brands.every((b) => selectedBrands.includes(b));
  const noBrandsSelected = selectedBrands.length === 0;
  const allGradesSelected =
    grades.length > 0 && grades.every((g) => selectedGrades.includes(g));
  const noGradesSelected = selectedGrades.length === 0;

  const visibleBrands =
    showAllBrands || brands.length <= DEFAULT_BRANDS_VISIBLE
      ? brands
      : brands.slice(0, DEFAULT_BRANDS_VISIBLE);
  const hiddenBrandCount = brands.length - visibleBrands.length;

  const hasFR = grades.includes(PALLET_GRADE_LABEL);

  return (
    <div
      className="rounded-md border border-border/60 bg-card overflow-hidden"
      data-testid={`category-block-${catId}`}
    >
      {/* Header row — click anywhere to expand/collapse. Does NOT toggle filters. */}
      <button
        type="button"
        onClick={onToggleExpanded}
        className="flex items-center gap-2 w-full px-2 py-1.5 hover:bg-muted/40 transition-colors text-left"
        aria-expanded={isExpanded}
        data-testid={`toggle-subcategory-${catId}`}
      >
        <div
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded overflow-hidden"
          title={categoryName}
        >
          {aiIconUrl ? (
            <img
              src={aiIconUrl}
              alt=""
              className="w-full h-full object-cover mix-blend-multiply dark:mix-blend-normal dark:brightness-90"
            />
          ) : (
            <CategoryIcon className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <span
          className="flex-1 text-sm font-medium text-foreground truncate"
          data-testid={`text-subcategory-name-${catId}`}
        >
          {categoryName}
        </span>
        <span className="text-xs text-muted-foreground tabular-nums">
          ({totalCount})
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {isExpanded && (
        <div className="px-2 pb-2.5 pt-0.5 border-t border-border/40">
          {/* Brands sub-group */}
          {brands.length > 0 && (
            <div className="pt-2">
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                  data-testid={`label-brands-${catId}`}
                >
                  Brands
                </span>
                <div className="flex items-center gap-1.5 text-[10px]">
                  <button
                    type="button"
                    onClick={onSelectAllBrands}
                    disabled={allBrandsSelected}
                    className="text-primary hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                    data-testid={`button-select-all-brands-${catId}`}
                  >
                    Select all
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    type="button"
                    onClick={onClearBrands}
                    disabled={noBrandsSelected}
                    className="text-primary hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                    data-testid={`button-clear-brands-${catId}`}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {visibleBrands.map((brand) => {
                  const id = `brand-${catId}-${brand}`;
                  const checked = selectedBrands.includes(brand);
                  const count = brandCounts[brand] ?? 0;
                  return (
                    <div key={brand} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={checked}
                        onCheckedChange={() => onToggleBrand(brand)}
                        data-testid={`filter-brand-${catId}-${brand}`}
                      />
                      <Label
                        htmlFor={id}
                        className="flex-1 text-sm leading-none cursor-pointer flex items-baseline justify-between gap-2"
                      >
                        <span className="truncate">{brand}</span>
                        <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">
                          ({count})
                        </span>
                      </Label>
                    </div>
                  );
                })}
                {hiddenBrandCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllBrands(true)}
                    className="text-xs text-primary hover:underline mt-0.5"
                    data-testid={`button-show-all-brands-${catId}`}
                  >
                    + Show all ({brands.length})
                  </button>
                )}
                {showAllBrands && brands.length > DEFAULT_BRANDS_VISIBLE && (
                  <button
                    type="button"
                    onClick={() => setShowAllBrands(false)}
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline mt-0.5"
                    data-testid={`button-show-fewer-brands-${catId}`}
                  >
                    Show fewer
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Condition sub-group */}
          {grades.length > 0 && (
            <div className="pt-3 mt-2 border-t border-border/40">
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                  data-testid={`label-condition-${catId}`}
                >
                  Condition
                </span>
                {grades.length > 1 && (
                  <div className="flex items-center gap-1.5 text-[10px]">
                    <button
                      type="button"
                      onClick={onSelectAllGrades}
                      disabled={allGradesSelected}
                      className="text-primary hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                      data-testid={`button-select-all-grades-${catId}`}
                    >
                      Select all
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <button
                      type="button"
                      onClick={onClearGrades}
                      disabled={noGradesSelected}
                      className="text-primary hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                      data-testid={`button-clear-grades-${catId}`}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                {grades.map((grade) => {
                  const id = `grade-${catId}-${grade}`;
                  const checked = selectedGrades.includes(grade);
                  const count = gradeCounts[grade] ?? 0;
                  const isFR = grade === PALLET_GRADE_LABEL;
                  return (
                    <div key={grade} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={checked}
                        onCheckedChange={() => onToggleGrade(grade)}
                        data-testid={`filter-grade-${catId}-${grade}`}
                      />
                      <Label
                        htmlFor={id}
                        className={`flex-1 text-sm leading-none cursor-pointer flex items-baseline justify-between gap-2 ${
                          isFR
                            ? "text-emerald-700 dark:text-emerald-400 font-medium"
                            : ""
                        }`}
                      >
                        <span className="truncate flex items-center gap-1.5">
                          {isFR && (
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"
                              aria-hidden
                            />
                          )}
                          {grade}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums flex-shrink-0">
                          ({count})
                        </span>
                      </Label>
                    </div>
                  );
                })}
                {hasFR && (
                  <Link
                    href={FACTORY_REFURBISHED_GRADING_ANCHOR}
                    className="block text-[10px] text-emerald-700 dark:text-emerald-400 underline hover:text-emerald-900 dark:hover:text-emerald-300 mt-1 ml-6"
                    data-testid={`link-factory-refurbished-explainer-${catId}`}
                  >
                    Learn more →
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const SubcategoryFilterBlock = memo(SubcategoryFilterBlockInner);
